import "./ProyectosCommon.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { CatalogoItemResponse } from "../../../shared/response/proyectos/CatalogoItemResponse";
import { FaseResponse } from "../../../shared/response/proyectos/FaseResponse";
import { ItemPropioPayload } from "../../../shared/services/api/payload/proyectos/ItemPropioPayload";
import { ItemPropioSaveService } from "../../services/proyectos/ItemPropioSaveService";
import { fmtCentavos } from "../../../shared/utils/proyectosFormat";

const TIPOP_OPCIONES: { value: string; label: string }[] = [
  { value: "fijo", label: "Fijo — precio estático" },
  { value: "ipc", label: "IPC — escala con la tasa de la cosecha vigente" },
  { value: "smmlv", label: "SMMLV — usa el factor jornal del snapshot activo" },
  { value: "volatil", label: "Volátil — insumo atado a dólar, precio manual" },
];
const FREQ_UNIDADES = ["apps", "meses", "veces", "visitas"];

/** Valores con los que arranca el bloque plegado: cubren el caso normal sin que nadie los toque. */
const DEFAULT_TIPOP = "fijo";
const DEFAULT_UNIDAD = "COP/Und";
const DEFAULT_CANT_UNIDAD = "Und/Ha";

/**
 * Agrega al paquete del beneficiario un ítem que **no está en el catálogo maestro** de su cultivo (la
 * pijama para tres plantas, por ejemplo). El ítem entra al presupuesto como cualquier otro, pero no queda
 * en el catálogo del cultivo: es de ese beneficiario.
 *
 * Tiene dos caminos, y el orden importa:
 *
 * 1. **Reusar.** Mientras se escribe el nombre se buscan ítems propios parecidos del mismo cultivo. Si
 *    aparece uno, se ofrece usarlo en vez de crear otro igual. Esto es lo que evita que el módulo se
 *    vuelva un depósito de cincuenta filas llamadas "pijama": un ítem con 23 dueños es una señal clara de
 *    que ya se ganó un puesto en el catálogo maestro; veintitrés ítems distintos no son señal de nada.
 * 2. **Crear.** Cinco campos a la vista -nombre, precio, cantidad, fase y motivo- y el resto plegado en
 *    "Configuración" con un valor por defecto ya puesto.
 *
 * **Por qué solo cinco a la vista.** Este formulario nació copiado del catálogo maestro, donde pedir
 * subsección, categoría y las dos unidades sí se justifica: ese ítem lo van a ver todos los proyectos del
 * cultivo y hay que poder encontrarlo. Un ítem de un beneficiario no carga eso. De los once campos, los
 * únicos que cambian algo son el nombre, el precio, la cantidad, la fase -que decide si es CAPEX u OPEX- y
 * el motivo; los otros seis son etiquetas de pantalla o tienen un valor que sirve casi siempre. El
 * servidor tampoco los exige: acepta subsección, categoría y unidades vacías.
 *
 * `Inclusión` y `Nitrofert` no se piden en ningún caso: los fija el servidor.
 *
 * El **motivo** es obligatorio en los dos caminos, y también en el servidor. Es lo único que después
 * distingue un ítem que de verdad falta en el catálogo de uno que alguien creó por no haber buscado bien.
 */
export class ItemPropioFormModal extends BaseSmallModalFormComponent<
  CatalogoItemResponse,
  ItemPropioPayload,
  ItemPropioSaveService
> {
  private _nombre!: HTMLInputElement;
  private _fase!: HTMLSelectElement;
  private _sub!: HTMLInputElement;
  private _subsDatalist!: HTMLDataListElement;
  private _categoria!: HTMLInputElement;
  private _tipoP!: HTMLSelectElement;
  private _cantidadPorHa!: HTMLInputElement;
  private _unidad!: HTMLInputElement;
  private _cantUnidad!: HTMLInputElement;
  private _freq!: HTMLInputElement;
  private _freqUnidad!: HTMLSelectElement;
  private _precio!: HTMLInputElement;
  private _precioNota!: HTMLElement;
  private _motivo!: HTMLInputElement;
  private _resumen!: HTMLElement;
  private _parecidosBox!: HTMLElement;
  private _bloqueNuevo!: HTMLElement;
  private _bloqueReuso!: HTMLElement;

  /** Ítem propio existente que se va a reusar; null = se crea uno nuevo. */
  private _reusar: CatalogoItemResponse | null = null;
  private _buscarTimer: number | undefined;
  /** Una vez que alguien escribe su propia subsección, cambiar de fase ya no se la pisa. */
  private _subTocado = false;

  /**
   * @param _subsPorFase Subsecciones que ya existen en el catálogo, por fase y de más usada a menos. Sirven
   *   para que el ítem propio caiga en un grupo que ya existe en vez de inventar uno nuevo por cada uno.
   */
  constructor(
    private readonly _cultivoId: string,
    private readonly _clienteId: string,
    private readonly _clienteNombre: string,
    private readonly _fases: FaseResponse[],
    private readonly _categoriasSugeridas: string[],
    private readonly _subsPorFase: Map<string, string[]>,
    private readonly _onSavedCb: (item: CatalogoItemResponse) => void,
  ) {
    super(new ItemPropioSaveService());
  }

  protected get title(): string { return "Agregar ítem propio del beneficiario"; }
  protected get saveText(): string { return "Agregar al paquete"; }

  /** Formulario: nombre, precio, cantidad, fase y motivo a la vista; el resto, plegado y con su valor por defecto. */
  protected renderBody(): string {
    return `
      <div class="cl-form-fields">
        <div class="pr-form-note" style="margin-bottom:4px">
          Este ítem queda solo para <strong>${this._esc(this._clienteNombre)}</strong> y suma al presupuesto.
          <strong>No</strong> entra al catálogo del cultivo.
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="ip-nombre">Nombre del ítem <span class="cl-required">*</span></label>
          <input id="ip-nombre" class="sx-input" type="text" maxlength="200" placeholder="Ej: Pijama para plantas">
        </div>

        <div id="ip-parecidos" hidden></div>

        <div id="ip-reuso" hidden></div>

        <div id="ip-nuevo">
          <div class="pr-form-row">
            <div class="cl-form-group">
              <label class="cl-form-label" for="ip-precio">Precio unitario (COP) <span class="cl-required">*</span></label>
              <input id="ip-precio" class="sx-input sx-input--numeric" type="number" min="0" step="1" placeholder="Ej: 100000">
              <span class="pr-form-note" id="ip-precio-nota"></span>
            </div>
            <div class="cl-form-group">
              <label class="cl-form-label" for="ip-qty">Cantidad por hectárea <span class="cl-required">*</span></label>
              <input id="ip-qty" class="sx-input sx-input--numeric" type="number" min="0" step="any" value="1">
              <span class="pr-form-note">El total es precio × cantidad × hectáreas del proyecto.</span>
            </div>
          </div>

          <div class="cl-form-group">
            <label class="cl-form-label" for="ip-fase">Fase <span class="cl-required">*</span></label>
            <select id="ip-fase" class="sx-input">
              ${this._fases.map((f) => `<option value="${f.id}">${f.codigo} — ${this._esc(f.nombre)}</option>`).join("")}
            </select>
            <span class="pr-form-note">Decide si el ítem entra como CAPEX o como OPEX.</span>
          </div>

          <details class="pr-adv" id="ip-avanzado">
            <summary>Configuración <span class="pr-adv-res" id="ip-adv-res"></span></summary>
            <div class="pr-adv-body">
              <div class="pr-form-row">
                <div class="cl-form-group">
                  <label class="cl-form-label" for="ip-tipop">Tipo de pago</label>
                  <select id="ip-tipop" class="sx-input">
                    ${TIPOP_OPCIONES.map((o) => `<option value="${o.value}"${o.value === DEFAULT_TIPOP ? " selected" : ""}>${o.label}</option>`).join("")}
                  </select>
                </div>
                <div class="cl-form-group">
                  <label class="cl-form-label" for="ip-sub">Subsección</label>
                  <input id="ip-sub" class="sx-input" type="text" maxlength="150" list="ip-subs" placeholder="Ej: Labores culturales">
                  <datalist id="ip-subs"></datalist>
                  <span class="pr-form-note">Grupo dentro de la fase donde aparece la fila.</span>
                </div>
              </div>
              <div class="pr-form-row">
                <div class="cl-form-group">
                  <label class="cl-form-label" for="ip-unidad">Unidad de precio</label>
                  <input id="ip-unidad" class="sx-input" type="text" maxlength="50" list="ip-unidades" value="${DEFAULT_UNIDAD}">
                  <datalist id="ip-unidades"><option value="COP/Kg"><option value="COP/Und"><option value="COP/Jornal"><option value="COP/L"><option value="COP/Ha Año"></datalist>
                </div>
                <div class="cl-form-group">
                  <label class="cl-form-label" for="ip-cantunidad">Unidad de cantidad</label>
                  <input id="ip-cantunidad" class="sx-input" type="text" maxlength="50" list="ip-cantunidades" value="${DEFAULT_CANT_UNIDAD}">
                  <datalist id="ip-cantunidades"><option value="Kg/Ha"><option value="L/Ha"><option value="Und/Ha"><option value="Jornal/Ha"><option value="M/Ha"><option value="Global"><option value="Visita"></datalist>
                </div>
              </div>
              <div class="pr-form-row">
                <div class="cl-form-group">
                  <label class="cl-form-label" for="ip-cat">Categoría</label>
                  <input id="ip-cat" class="sx-input" type="text" maxlength="100" list="ip-cats" placeholder="Ej: Insumos">
                  <datalist id="ip-cats">${this._categoriasSugeridas.map((c) => `<option value="${this._esc(c)}">`).join("")}</datalist>
                </div>
                <div class="cl-form-group">
                  <label class="cl-form-label" for="ip-freq">Frecuencia</label>
                  <div style="display:flex;gap:6px">
                    <input id="ip-freq" class="sx-input sx-input--numeric" type="number" min="0" step="any" value="1" style="flex:1">
                    <select id="ip-frequnidad" class="sx-input" style="flex:1">
                      <option value="">— sin unidad —</option>
                      ${FREQ_UNIDADES.map((u) => `<option value="${u}">${u}</option>`).join("")}
                    </select>
                  </div>
                  <span class="pr-form-note">Repeticiones dentro de la fase.</span>
                </div>
              </div>
            </div>
          </details>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="ip-motivo">Por qué lo necesita <span class="cl-required">*</span></label>
          <input id="ip-motivo" class="sx-input" type="text" maxlength="200" placeholder="Ej: tiene 3 plantas madre que hay que proteger del granizo">
          <span class="pr-form-note">Corto y concreto. Es lo que se revisa después para decidir si el ítem debe pasar al catálogo del cultivo.</span>
        </div>
      </div>
    `;
  }

  /** Cablea los campos, pone los valores por defecto que dependen de la fase y arranca el foco en el nombre. */
  protected override onAfterRender(el: HTMLDivElement): void {
    this._nombre        = el.querySelector<HTMLInputElement>("#ip-nombre")!;
    this._fase          = el.querySelector<HTMLSelectElement>("#ip-fase")!;
    this._sub           = el.querySelector<HTMLInputElement>("#ip-sub")!;
    this._subsDatalist  = el.querySelector<HTMLDataListElement>("#ip-subs")!;
    this._categoria     = el.querySelector<HTMLInputElement>("#ip-cat")!;
    this._tipoP         = el.querySelector<HTMLSelectElement>("#ip-tipop")!;
    this._cantidadPorHa = el.querySelector<HTMLInputElement>("#ip-qty")!;
    this._unidad        = el.querySelector<HTMLInputElement>("#ip-unidad")!;
    this._cantUnidad    = el.querySelector<HTMLInputElement>("#ip-cantunidad")!;
    this._freq          = el.querySelector<HTMLInputElement>("#ip-freq")!;
    this._freqUnidad    = el.querySelector<HTMLSelectElement>("#ip-frequnidad")!;
    this._precio        = el.querySelector<HTMLInputElement>("#ip-precio")!;
    this._precioNota    = el.querySelector<HTMLElement>("#ip-precio-nota")!;
    this._motivo        = el.querySelector<HTMLInputElement>("#ip-motivo")!;
    this._resumen       = el.querySelector<HTMLElement>("#ip-adv-res")!;
    this._parecidosBox  = el.querySelector<HTMLElement>("#ip-parecidos")!;
    this._bloqueNuevo   = el.querySelector<HTMLElement>("#ip-nuevo")!;
    this._bloqueReuso   = el.querySelector<HTMLElement>("#ip-reuso")!;

    this._nombre.addEventListener("input", () => {
      // Escribir otro nombre cancela el reuso: lo que se ofreció era para el nombre anterior.
      if (this._reusar) this._cancelarReuso();
      this._validate();
      this._buscarParecidosDebounced();
    });
    this._fase.addEventListener("change", () => this._aplicarSubDeFase());
    this._sub.addEventListener("input", () => { this._subTocado = true; this._pintarResumen(); });
    [this._categoria, this._unidad, this._cantUnidad].forEach((i) =>
      i.addEventListener("input", () => this._pintarResumen()));
    this._tipoP.addEventListener("change", () => { this._aplicarTipoPago(); this._validate(); });
    this._freqUnidad.addEventListener("change", () => this._pintarResumen());
    this._freq.addEventListener("input", () => { this._pintarResumen(); this._validate(); });
    [this._cantidadPorHa, this._precio, this._motivo].forEach((i) =>
      i.addEventListener("input", () => this._validate()));

    this._aplicarSubDeFase();
    this._aplicarTipoPago();
    this._validate();
    this._nombre.focus();
  }

  /**
   * Lo que se manda a `GuardarItemPropio`. Con un ítem parecido elegido para reusar va su id (`catalogoItemId`) y el
   * servidor no crea otro; el precio se redondea hacia abajo a pesos y nunca es negativo.
   */
  protected buildPayload(): ItemPropioPayload {
    return {
      id: null,
      clienteId: this._clienteId,
      motivo: this._motivo.value.trim(),
      catalogoItemId: this._reusar?.id ?? null,
      cultivoId: this._cultivoId,
      faseId: this._fase.value,
      sub: this._sub.value.trim(),
      nombre: this._nombre.value.trim(),
      categoria: this._categoria.value.trim(),
      unidad: this._unidad.value.trim(),
      cantUnidad: this._cantUnidad.value.trim(),
      cantidadPorHa: Number(this._cantidadPorHa.value) || 0,
      tipoP: this._tipoP.value,
      precioReferencial: this._precio.value.trim() === "" ? null : Math.max(0, Math.floor(Number(this._precio.value))),
      freq: Number(this._freq.value) || 1,
      freqUnidad: this._freqUnidad.value || null,
    };
  }

  protected override onSaved(item: CatalogoItemResponse): void {
    this._onSavedCb(item);
    this.dispose();
  }

  // ── Configuración plegada ───────────────────────────────────

  /**
   * Deja en la subsección la más usada de la fase elegida, para que la fila caiga en un grupo que ya existe
   * y no se abra una sección nueva por cada ítem propio. Si alguien ya escribió la suya, no se la pisa.
   */
  private _aplicarSubDeFase(): void {
    const subs = this._subsPorFase.get(this._fase.value) ?? [];
    this._subsDatalist.innerHTML = subs.map((s) => `<option value="${this._esc(s)}">`).join("");
    if (!this._subTocado) this._sub.value = subs[0] ?? "";
    this._pintarResumen();
  }

  /**
   * Con SMMLV el precio lo pone el factor jornal del snapshot, así que el campo se bloquea en vez de dejar
   * escribir un número que el servidor va a ignorar.
   */
  private _aplicarTipoPago(): void {
    const esJornal = this._tipoP.value === "smmlv";
    this._precio.disabled = esJornal;
    this._precio.placeholder = esJornal ? "Lo pone el jornal" : "Ej: 100000";
    this._precioNota.textContent = esJornal ? "Con SMMLV el precio sale del factor jornal del snapshot activo." : "";
    this._pintarResumen();
  }

  /** Resume en el encabezado lo que quedó adentro del bloque, para no tener que abrirlo solo por revisar. */
  private _pintarResumen(): void {
    const tipo = TIPOP_OPCIONES.find((o) => o.value === this._tipoP.value)?.label.split(" — ")[0] ?? this._tipoP.value;
    const freq = Number(this._freq.value) || 1;
    const partes = [tipo, this._unidad.value.trim(), this._cantUnidad.value.trim(), this._sub.value.trim()]
      .filter((p) => p.length > 0);
    if (freq !== 1) partes.push(`×${freq}`);
    this._resumen.textContent = partes.length > 0 ? `· ${partes.join(" · ")}` : "";
  }

  // ── Reuso ───────────────────────────────────────────────────

  /** Espera a que la escritura se calme antes de preguntarle al servidor por ítems parecidos. */
  private _buscarParecidosDebounced(): void {
    window.clearTimeout(this._buscarTimer);
    const nombre = this._nombre.value.trim();
    if (nombre.length < 3) { this._parecidosBox.hidden = true; return; }
    this._buscarTimer = window.setTimeout(() => void this._buscarParecidos(nombre), 400);
  }

  /** Pinta los ítems propios parecidos del cultivo, con el botón para usar uno en vez de crear otro igual. */
  private async _buscarParecidos(nombre: string): Promise<void> {
    const encontrados = await this.saveService.buscarParecidos(this._cultivoId, nombre);
    // El nombre pudo cambiar mientras la consulta iba en camino.
    if (this._nombre.value.trim() !== nombre) return;
    if (encontrados.length === 0) { this._parecidosBox.hidden = true; return; }

    this._parecidosBox.innerHTML = `
      <div class="pr-alert-warn" style="margin:0 0 10px">
        <strong>Ya existe${encontrados.length === 1 ? "" : "n"} ${encontrados.length} ítem${encontrados.length === 1 ? "" : "es"} propio${encontrados.length === 1 ? "" : "s"} parecido${encontrados.length === 1 ? "" : "s"}</strong>
        <div style="margin-top:4px;font-size:var(--sx-text-xs)">Usar el que ya existe mantiene el catálogo revisable. Crear otro igual no.</div>
        ${encontrados.map((it) => `
          <div style="display:flex;align-items:center;gap:8px;margin-top:8px">
            <div style="flex:1">
              <strong>${this._esc(it.nombre)}</strong>
              <div style="font-size:var(--sx-text-xs)">
                ${this._esc(it.categoria || "")}${it.cantUnidad ? " · " + this._esc(it.cantUnidad) : ""} · ${it.tipoP}
                ${it.precioReferencial ? " · " + fmtCentavos(it.precioReferencial) : " · sin precio"}
                · ${it.duenos} beneficiario${it.duenos === 1 ? "" : "s"}
              </div>
            </div>
            <button class="sx-btn sx-btn-ghost sx-btn-sm" type="button" data-usar="${it.id}">Usar este</button>
          </div>`).join("")}
      </div>
    `;
    this._parecidosBox.hidden = false;
    this._parecidosBox.querySelectorAll<HTMLButtonElement>("[data-usar]").forEach((btn) => {
      btn.onclick = () => this._activarReuso(encontrados.find((x) => x.id === btn.dataset.usar)!);
    });
  }

  /** Pasa al modo reuso: el ítem ya existe, así que solo falta decir por qué lo necesita este beneficiario. */
  private _activarReuso(item: CatalogoItemResponse): void {
    this._reusar = item;
    this._parecidosBox.hidden = true;
    this._bloqueNuevo.hidden = true;
    this._bloqueReuso.innerHTML = `
      <div class="pr-panel" style="padding:10px 14px;margin-bottom:10px">
        <div style="display:flex;align-items:center;gap:8px">
          <div style="flex:1">
            <strong>${this._esc(item.nombre)}</strong>
            <div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">
              Se reusa el ítem que ya existe · ${this._esc(item.categoria || "")}${item.cantUnidad ? " · " + this._esc(item.cantUnidad) : ""} · ${item.tipoP}
              ${item.precioReferencial ? " · " + fmtCentavos(item.precioReferencial) : " · sin precio"}
            </div>
          </div>
          <button class="sx-btn sx-btn-ghost sx-btn-sm" type="button" id="ip-cancelar-reuso">Crear uno nuevo</button>
        </div>
      </div>
    `;
    this._bloqueReuso.hidden = false;
    this._bloqueReuso.querySelector<HTMLButtonElement>("#ip-cancelar-reuso")!.onclick = () => {
      this._cancelarReuso();
      this._validate();
    };
    this._motivo.focus();
    this._validate();
  }

  /** Vuelve al modo crear: los campos del ítem nuevo reaparecen tal como estaban. */
  private _cancelarReuso(): void {
    this._reusar = null;
    this._bloqueReuso.hidden = true;
    this._bloqueNuevo.hidden = false;
  }

  // ── Validación ──────────────────────────────────────────────

  /**
   * Solo exige lo que de verdad cambia algo: nombre, precio, cantidad y motivo -la fase siempre tiene una
   * seleccionada-. Subsección, categoría y las dos unidades son etiquetas, el servidor las acepta vacías,
   * así que no bloquean el botón.
   *
   * El precio sí se exige, aunque el servidor lo acepte nulo: el ítem propio existe para sumarle plata al
   * presupuesto, y sin precio entraría como "sin precio" y no sumaría nada, que es justo lo contrario de lo
   * que se pidió. La excepción es SMMLV, donde el precio lo pone el factor jornal.
   */
  private _validate(): void {
    const motivoOk = this._motivo.value.trim().length >= 1;
    // Reusando, los campos del ítem los pone el ítem que ya existe: solo hace falta el motivo.
    const precioOk = this._tipoP.value === "smmlv" || Number(this._precio.value) > 0;
    const ok = this._reusar
      ? motivoOk
      : motivoOk
        && this._nombre.value.trim().length >= 1
        && precioOk
        && Number(this._cantidadPorHa.value) > 0
        && Number(this._freq.value) > 0;
    this.setSaveDisabled(!ok);
  }

  /** Escapa `&`, `"` y `<` para meter texto del usuario en el HTML del formulario. */
  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
